import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  getBatches,
  getJobStatusCounts,
  type FetchBatchesParams,
  type FetchJobStatusCountsParams,
} from '../services/catalog-operations.service';
import type { BatchRecord } from '../types/catalog-operations.types';

import { BATCH_RUNS_POLL_MS } from './use-batch-retry';

/** Fetches the paginated batches listing. The `params` object is the cache
 *  key — every tab/search/page permutation gets its own slot. Previous data
 *  is preserved across pagination/filter changes to keep the table from
 *  flickering between fetches. */
export function useBatches(params: FetchBatchesParams) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batches(params as Record<string, unknown>),
    queryFn: () => getBatches(params),
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    // A retrying / paused / running batch moves on its own; keep its row status honest.
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (b) =>
          b.batchStatus === 'IN_PROGRESS' ||
          b.batchStatus === 'RETRYING' ||
          b.batchStatus === 'PAUSED',
      )
        ? BATCH_RUNS_POLL_MS
        : false,
  });
}

/** Trial-batch existence probe. `archived: 'ALL'` on purpose: the default list
 *  hides archived rows, and an archived trial batch must still count as "the
 *  trial's shared batch exists" — otherwise archiving it would re-open the
 *  create-trial entry points. Server-side `source=TRIAL` keeps the payload tiny
 *  (a subsidiary has at most a handful of trial batches). */
const TRIAL_BATCH_PROBE_PARAMS: FetchBatchesParams = {
  source: 'TRIAL',
  page: 0,
  size: 20,
  archived: 'ALL',
};

/** Every trial-sourced batch for the subsidiary, archived or not — single
 *  source of truth for the trial-batch gates on the Catalog Operations and
 *  Onboarding pages. */
export function useTrialBatches(): { items: BatchRecord[]; isLoading: boolean } {
  const { data, isLoading } = useBatches(TRIAL_BATCH_PROBE_PARAMS);
  return { items: data?.items ?? [], isLoading };
}

/** True when the subsidiary has any trial-sourced batch (any status, archived
 *  or not). Used to block creating a second trial batch across entry points. */
export function useHasTrialBatch(): boolean {
  return useTrialBatches().items.length > 0;
}

/** Fetches per-status job counts for the tab strip. Keyed on the same
 *  non-status filters as `useBatches` so a filter change invalidates both
 *  queries together and the tab badges never drift from the list beneath
 *  them; deliberately blind to the active tab. `enabled` lets admin-only
 *  callers (the Archived badge count) skip the request for members. */
export function useJobStatusCounts(
  params: FetchJobStatusCountsParams,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.statusCounts(params as Record<string, unknown>),
    queryFn: () => getJobStatusCounts(params),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    enabled: options.enabled ?? true,
  });
}
